/* =========================================================
   «Revisé lo mío y lo doy por bueno.»

   Hasta ahora el proceso no tenía final. El socio rellenaba, llegaba al
   100 % y ahí se quedaba la cosa: nadie del lado de ETC podía distinguir
   a quien había terminado de quien se fue a media faena y no ha vuelto.
   Con una fecha de cierre encima, esa diferencia es el trabajo entero
   del equipo, y hasta hoy se resolvía abriendo fichas una por una.

   El botón vive debajo del aviso de «ya no te faltan campos» porque es
   ahí donde la persona está mirando cuando acaba. No en el panel, no en
   un menú: al final de la lista que acaba de vaciar.

   Quien decide si está completa es el SERVIDOR, no esta pantalla. El
   porcentaje de la ficha se calcula en local para que el número se mueva
   mientras se escribe, y ese número es bueno para orientar pero no para
   declarar. Por eso esto pregunta aparte, y por eso un 409 del servidor
   —«todavía falta»— se enseña tal cual en vez de esconderlo: si las dos
   cuentas no coinciden, la que vale es la de allá.
   ========================================================= */
import { useCallback, useEffect, useState } from 'react';
import { ic } from '@/components/Icons';
import { Modal } from './Modal';
import { api, ApiError } from '@/lib/api';
import { useI18n } from '@/i18n';
import { usePuedeEditar } from '@/hooks/usePuedeEditar';
import { useAppStore } from '@/store/useAppStore';

interface EstadoVB {
  dadoEn: string;
  pct: number | null;
  quien: string | null;
}

interface Respuesta {
  progreso: { pct: number; hechos: number; total: number };
  vistoBueno: EstadoVB | null;
}

const fecha = (iso: string, lang: string) =>
  new Date(iso).toLocaleDateString(lang === 'es' ? 'es-MX' : 'en-US', {
    day: 'numeric', month: 'long', year: 'numeric',
  });

/** @param faltan campos pendientes de lo GUARDADO, sin contar el borrador.
 *                Ver la nota en CajaPendientesOrg: el visto bueno habla de
 *                lo que está escrito, no de lo que se está escribiendo. */
export function VistoBueno({ orgId, faltan }: { orgId: string; faltan: number }) {
  const { lang } = useI18n();
  const es = lang !== 'en';
  const permiso = usePuedeEditar('org');
  const role = useAppStore((s) => s.role);
  const addToast = useAppStore((s) => s.addToast);

  const [estado, setEstado] = useState<Respuesta | null>(null);
  const [preguntando, setPreguntando] = useState(false);
  const [enviando, setEnviando] = useState(false);

  /* Se pregunta al abrir y cada vez que la ficha CRUZA el 100 %, no cada
     vez que cambia el número de pendientes: ese número baja con cada
     tecla mientras el socio escribe, y atarse a él era una petición por
     pulsación —y cada una recalcula la ficha entera en el servidor—.
     Lo único que hace falta saber aquí es de qué lado del 100 % está. */
  const completaEnLocal = faltan === 0;

  const consultar = useCallback(() => {
    let vivo = true;
    api.get<Respuesta>(`/api/organizaciones/${encodeURIComponent(orgId)}/progreso`)
      .then((r) => { if (vivo) setEstado(r); })
      /* En modo demostración la API no existe y esto devuelve 404. No es
         un fallo que haya que enseñar: sin servidor no hay visto bueno
         que dar, así que la tarjeta simplemente no aparece. */
      .catch(() => { if (vivo) setEstado(null); });
    return () => { vivo = false; };
  }, [orgId]);

  useEffect(() => consultar(), [consultar, completaEnLocal]);

  if (!estado) return null;

  const { progreso, vistoBueno: vb } = estado;
  /* Las dos cuentas tienen que coincidir para ofrecer el botón. La local
     va al día con lo que se escribe; la del servidor es la que manda al
     aceptar. Si discrepan, gana el «todavía no», y el 409 lo explica. */
  const completa = completaEnLocal && progreso.hechos >= progreso.total;
  const esSocio = role !== 'ETC_ADMIN';
  const puedeDarlo = esSocio && permiso.puede;
  const desactualizado = !!vb && !completa;

  // Sin visto bueno y sin poder darlo, no hay nada que contar que la
  // lista de pendientes no diga ya.
  if (!vb && !puedeDarlo && !completa) return null;

  async function confirmar() {
    setEnviando(true);
    try {
      const r = await api.post<Respuesta>('/api/visto-bueno', { orgId });
      setEstado(r);
      setPreguntando(false);
      addToast({
        kind: 'ok',
        title: es ? 'Visto bueno registrado' : 'Approval recorded',
        body: es
          ? 'ETC ya sabe que tu actualización está terminada. Puedes seguir editando si hace falta.'
          : 'ETC now knows your update is finished. You can still keep editing if needed.',
      });
    } catch (e) {
      const err = e as ApiError;
      const cuerpo = err.detail as { pct?: number; faltan?: number } | undefined;
      addToast({
        kind: 'err',
        title: es ? 'No se pudo registrar' : 'Could not record it',
        body: typeof cuerpo?.pct === 'number'
          ? (es
            ? `El servidor ve tu ficha al ${cuerpo.pct} %: quedan ${cuerpo.faltan} datos por completar. `
              + 'Recarga la pantalla para ver cuáles.'
            : `The server sees your record at ${cuerpo.pct} %: ${cuerpo.faltan} items are still missing. `
              + 'Reload the screen to see which ones.')
          : err.message,
      });
      setPreguntando(false);
    } finally {
      setEnviando(false);
    }
  }

  /* ---------------- lo que se pinta ---------------- */

  const marco = (fondo: string, borde: string, hijos: React.ReactNode) => (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        padding: '13px 18px', borderTop: '1px solid var(--line)',
        background: fondo, borderLeft: `3px solid ${borde}`,
      }}
    >
      {hijos}
    </div>
  );

  let bloque: React.ReactNode = null;

  if (vb) {
    const cuando = fecha(vb.dadoEn, lang);
    const quien = vb.quien ? ` · ${vb.quien}` : '';
    bloque = marco(
      desactualizado ? 'var(--st-edit-l)' : 'var(--st-read-l)',
      desactualizado ? 'var(--st-edit)' : 'var(--ok)',
      <>
        <span style={{ color: desactualizado ? 'var(--st-edit)' : 'var(--ok)', display: 'inline-flex' }}>
          {desactualizado ? ic.warn : ic.check}
        </span>
        <div style={{ flex: 1, minWidth: 200, fontSize: 13, lineHeight: 1.5 }}>
          <b>
            {es ? `Visto bueno dado el ${cuando}` : `Approved on ${cuando}`}
            {quien}
          </b>
          {desactualizado ? (
            /* Sin repetir aquí cuántos campos faltan: eso lo dice la
               lista de arriba, que va al día con cada tecla. Dos cuentas
               del mismo número acaban discrepando. */
            <div style={{ marginTop: 3, fontSize: 12 }}>
              {es
                ? 'Desde entonces la ficha dejó de estar completa. Rellena lo que aparece '
                  + 'arriba y vuelve a confirmar para que ETC lo sepa.'
                : 'The record is no longer complete. Fill in what is listed above and '
                  + 'confirm again so ETC knows.'}
            </div>
          ) : null}
        </div>
        {puedeDarlo && completa ? (
          <button className="btn" onClick={() => setPreguntando(true)}>
            {es ? 'Volver a confirmar' : 'Confirm again'}
          </button>
        ) : null}
      </>,
    );
  } else if (completa && puedeDarlo) {
    bloque = marco('var(--st-read-l)', 'var(--ok)', (
      <>
        <div style={{ flex: 1, minWidth: 200, fontSize: 13, lineHeight: 1.5 }}>
          <b>{es ? '¿Ya está todo revisado?' : 'Everything reviewed?'}</b>{' '}
          {es
            ? 'Confírmalo y ETC recibirá el aviso de que tu actualización está terminada. '
              + 'No cierra nada: puedes seguir editando hasta la fecha de cierre.'
            : 'Confirm it and ETC will be told your update is finished. '
              + 'It locks nothing: you can keep editing until the deadline.'}
        </div>
        <button className="btn primary" onClick={() => setPreguntando(true)}>
          {es ? 'Doy mi visto bueno' : 'Give my approval'}
        </button>
      </>
    ));
  } else if (completa && !esSocio) {
    // Lo que ve ETC cuando la ficha está llena pero nadie la ha declarado.
    bloque = marco('var(--surface-2)', 'var(--line)', (
      <div style={{ flex: 1, fontSize: 13, color: 'var(--text-2)' }}>
        {es
          ? 'La ficha está completa, pero el socio todavía no ha dado su visto bueno.'
          : 'The record is complete, but the partner has not approved it yet.'}
      </div>
    ));
  }

  return (
    <>
      {bloque}
      <Modal
        open={preguntando}
        onClose={() => { if (!enviando) setPreguntando(false); }}
        title={es ? 'Dar el visto bueno' : 'Give your approval'}
        footer={
          <>
            <button className="btn" onClick={() => setPreguntando(false)} disabled={enviando}>
              {es ? 'Cancelar' : 'Cancel'}
            </button>
            <button className="btn primary" onClick={confirmar} disabled={enviando}>
              {enviando
                ? (es ? 'Enviando…' : 'Sending…')
                : (es ? 'Sí, lo confirmo' : 'Yes, I confirm')}
            </button>
          </>
        }
      >
        <div style={{ fontSize: 13.5, lineHeight: 1.6 }}>
          <p style={{ marginTop: 0 }}>
            {es
              ? 'Estás declarando que revisaste los datos de tu organización, tus sedes y '
                + 'tus instructores, y que están correctos y al día.'
              : 'You are declaring that you have reviewed your organization, site and '
                + 'instructor data, and that it is correct and up to date.'}
          </p>
          <p>
            {es
              ? 'ETC recibirá un aviso con tu nombre y la fecha. Lo que NO hace: bloquear '
                + 'tu ficha. Puedes seguir corrigiendo lo que quieras hasta que el proceso '
                + 'cierre; si cambias algo, vuelve a confirmar.'
              : 'ETC will be notified with your name and the date. What it does NOT do: lock '
                + 'your record. You can keep correcting anything until the process closes; '
                + 'if you change something, confirm again.'}
          </p>
        </div>
      </Modal>
    </>
  );
}
